import { Link } from 'react-router-dom';
import { ArrowUpRight } from 'lucide-react';
import { Badge } from '@/components/ui/Badge';
import { EVENT_KIND_META } from '@/components/manufacturing/planning/shared';
import type { CalendarEvent, CalendarEventKind, CalendarEventSeverity } from '@/features/manufacturing/planning/types';
import { formatCurrency } from '@/lib/formatters';
import { cn } from '@/lib/utils';

/** Card border / tint for a vendor order group, by how urgent it is. */
export const GROUP_SEVERITY_SURFACE: Record<CalendarEventSeverity, string> = {
  critical: 'border-fresh-salmon-dark/60 bg-fresh-salmon/10',
  warning: 'border-info/50 bg-info-soft/15',
  info: 'border-border',
};

export const GROUP_SEVERITY_ICON: Record<CalendarEventSeverity, string> = {
  critical: 'border-fresh-salmon-dark/40 bg-fresh-salmon/30 text-fresh-salmon-dark',
  warning: 'border-info-soft bg-info-soft/50 text-info',
  info: 'border-border bg-secondary text-foreground',
};

/** Calendar-cell chip for a vendor order group. */
export const GROUP_SEVERITY_CHIP: Record<CalendarEventSeverity, string> = {
  critical: 'border-fresh-salmon-dark/60 bg-fresh-salmon/30 text-fresh-salmon-dark',
  warning: 'border-info/50 bg-info-soft/50 text-info',
  info: 'border-info-soft/70 bg-info-soft/25 text-info',
};

/** The one pseudo-kind this calendar adds on top of the engine's events. */
export const ORDER_GROUP_FILTER = 'orders' as const;
export type CalendarFilterKey = typeof ORDER_GROUP_FILTER | Exclude<CalendarEventKind, 'order_ideal'>;

export const FILTER_META: Record<CalendarFilterKey, { label: string; dot: string }> = {
  orders: { label: 'Orders to place', dot: 'bg-info' },
  order_latest: { label: EVENT_KIND_META.order_latest.label, dot: EVENT_KIND_META.order_latest.dot },
  runout: { label: EVENT_KIND_META.runout.label, dot: EVENT_KIND_META.runout.dot },
  po_order_by: { label: EVENT_KIND_META.po_order_by.label, dot: EVENT_KIND_META.po_order_by.dot },
  receipt_expected: { label: EVENT_KIND_META.receipt_expected.label, dot: EVENT_KIND_META.receipt_expected.dot },
  planned_receipt: { label: EVENT_KIND_META.planned_receipt.label, dot: EVENT_KIND_META.planned_receipt.dot },
  production: { label: EVENT_KIND_META.production.label, dot: EVENT_KIND_META.production.dot },
  holiday: { label: EVENT_KIND_META.holiday.label, dot: EVENT_KIND_META.holiday.dot },
};

export const ALL_FILTERS = Object.keys(FILTER_META) as CalendarFilterKey[];
/** Build days and "suggested order lands" are noise on a buying calendar; off until asked for. */
export const DEFAULT_FILTERS: CalendarFilterKey[] = ALL_FILTERS.filter((k) => k !== 'production' && k !== 'planned_receipt');

const LONG_DATE = new Intl.DateTimeFormat('en-US', {
  weekday: 'long',
  month: 'long',
  day: 'numeric',
  year: 'numeric',
  timeZone: 'UTC',
});

const WEEKDAY_DATE = new Intl.DateTimeFormat('en-US', {
  weekday: 'short',
  month: 'short',
  day: 'numeric',
  timeZone: 'UTC',
});

/** `Wednesday, November 4, 2026` */
export function longDate(date: string): string {
  return LONG_DATE.format(new Date(`${date.slice(0, 10)}T00:00:00Z`));
}

/** `Wed, Nov 4` */
export function weekdayDate(date: string): string {
  return WEEKDAY_DATE.format(new Date(`${date.slice(0, 10)}T00:00:00Z`));
}

export const MONTH_LONG = new Intl.DateTimeFormat('en-US', { month: 'long', year: 'numeric', timeZone: 'UTC' });

/** A non-order event in the day panel or agenda, with a jump to the part or PO behind it. */
export function EventRow({ event, onOpenItem }: { event: CalendarEvent; onOpenItem: (itemId: string) => void }) {
  const meta = EVENT_KIND_META[event.kind];
  const Icon = meta.icon;
  return (
    <li className="flex gap-3 py-3">
      <span className={cn('mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-md border', meta.chip)}>
        <Icon className="h-3.5 w-3.5" />
      </span>
      <div className="min-w-0 flex-1 space-y-0.5">
        <div className="flex flex-wrap items-center gap-2">
          <span className={cn('text-sm font-medium', event.severity === 'critical' && 'text-fresh-salmon-dark')}>{event.title}</span>
          {event.severity === 'critical' && <Badge variant="warning">Act now</Badge>}
          {event.amountCents != null && (
            <span className="text-xs text-muted-foreground tabular-nums">{formatCurrency(event.amountCents)}</span>
          )}
        </div>
        {event.detail && <p className="text-xs text-muted-foreground">{event.detail}</p>}
        <div className="flex flex-wrap gap-3 text-xs">
          {event.itemId && (
            <button
              type="button"
              className="inline-flex items-center gap-0.5 text-info hover:underline"
              onClick={() => onOpenItem(event.itemId!)}
            >
              Part details <ArrowUpRight className="h-3 w-3" />
            </button>
          )}
          {event.poId && (
            <Link
              to={`/procurement/purchase-orders?po=${event.poId}`}
              className="inline-flex items-center gap-0.5 text-info hover:underline"
            >
              Open purchase order <ArrowUpRight className="h-3 w-3" />
            </Link>
          )}
        </div>
      </div>
    </li>
  );
}
